import { Fragment, useEffect, useRef, useState } from "react";

import { MemberCpe } from "./MemberCpe.jsx";
import { MemberDues } from "./MemberDues.jsx";

const SEGMENTS = ["student", "professional", "senior"];
const STAGES = ["prospect", "attendee", "member", "volunteer", "lapsed"];
const TYPES = ["Annual", "Student", "Lifetime"];

async function api(path, options) {
  return fetch(path, {
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    ...options,
  });
}

const EMPTY = { name: "", email: "", segment: "", lifecycle_stage: "", membership_type: "", certifications: "", isc2_member_number: "", dietary_requirements: "" };

function AddForm({ chapterId, onAdded }) {
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await api(`/api/chapters/${chapterId}/members`, {
      method: "POST",
      body: JSON.stringify(form),
    });
    setBusy(false);
    if (res.ok) {
      setForm(EMPTY);
      onAdded();
    } else {
      const body = await res.json().catch(() => ({}));
      setError(body.error || "Could not add member.");
    }
  }

  return (
    <form className="form-card" onSubmit={submit}>
      <div className="form-grid">
        <label className="field">
          Name
          <input name="name" required value={form.name} onChange={set("name")} placeholder="Full name" />
        </label>
        <label className="field">
          Email
          <input type="email" name="email" value={form.email} onChange={set("email")} placeholder="optional" />
        </label>
        <label className="field">
          Segment
          <input name="segment" list="segments" value={form.segment} onChange={set("segment")} placeholder="professional" />
          <datalist id="segments">
            {SEGMENTS.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </label>
        <label className="field">
          Lifecycle stage
          <input name="lifecycle_stage" list="stages" value={form.lifecycle_stage} onChange={set("lifecycle_stage")} placeholder="member" />
          <datalist id="stages">
            {STAGES.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </label>
        <label className="field">
          Type
          <input name="membership_type" list="types" value={form.membership_type} onChange={set("membership_type")} placeholder="Annual" />
          <datalist id="types">
            {TYPES.map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>
        </label>
        <label className="field">
          Certifications
          <input name="certifications" value={form.certifications} onChange={set("certifications")} placeholder="e.g. CISSP, CCSP" />
        </label>
        <label className="field">
          ISC2 member number
          <input name="isc2_member_number" value={form.isc2_member_number} onChange={set("isc2_member_number")} placeholder="ISC2 ID" />
        </label>
        <label className="field" style={{ gridColumn: "1 / -1" }}>
          Dietary requirements
          <input name="dietary_requirements" value={form.dietary_requirements} onChange={set("dietary_requirements")} placeholder="e.g. vegetarian, nut allergy (for event catering)" />
        </label>
        <button className="btn-primary" style={{ width: "auto" }} type="submit" disabled={busy}>
          {busy ? "Adding…" : "Add member"}
        </button>
      </div>
      {error && <div className="err">{error}</div>}
    </form>
  );
}

function MemberEditForm({ member, onSaved, onCancel }) {
  const [form, setForm] = useState({
    name: member.name,
    email: member.email || "",
    segment: member.segment || "",
    lifecycle_stage: member.lifecycle_stage || "",
    membership_type: member.membership_type || "",
    certifications: member.certifications || "",
    isc2_member_number: member.isc2_member_number || "",
    joined_on: member.joined_on || "",
    dietary_requirements: member.dietary_requirements || "",
  });
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function submit(e) {
    e.preventDefault();
    const res = await api(`/api/members/${member.id}`, { method: "PATCH", body: JSON.stringify(form) });
    if (res.ok) onSaved();
    else setError((await res.json().catch(() => ({}))).error || "Could not save.");
  }

  return (
    <form className="form-card" onSubmit={submit}>
      <div className="form-grid">
        <label className="field">Name<input name="name" value={form.name} onChange={set("name")} /></label>
        <label className="field">Email<input type="email" name="email" value={form.email} onChange={set("email")} /></label>
        <label className="field">Segment<input name="segment" list="segments" value={form.segment} onChange={set("segment")} /></label>
        <label className="field">Stage<input name="lifecycle_stage" list="stages" value={form.lifecycle_stage} onChange={set("lifecycle_stage")} /></label>
        <label className="field">Type<input name="membership_type" list="types" value={form.membership_type} onChange={set("membership_type")} /></label>
        <label className="field">Certifications<input name="certifications" value={form.certifications} onChange={set("certifications")} placeholder="CISSP, CCSP" /></label>
        <label className="field">ISC2 member number<input name="isc2_member_number" value={form.isc2_member_number} onChange={set("isc2_member_number")} placeholder="ISC2 ID" /></label>
        <label className="field">Joined<input type="date" name="joined_on" value={form.joined_on || ""} onChange={set("joined_on")} /></label>
        <label className="field" style={{ gridColumn: "1 / -1" }}>Dietary requirements<input name="dietary_requirements" value={form.dietary_requirements} onChange={set("dietary_requirements")} placeholder="e.g. vegetarian, nut allergy" /></label>
      </div>
      <div className="field-actions">
        <button className="btn-primary" style={{ width: "auto" }} type="submit">Save</button>
        <button className="btn-ghost" type="button" onClick={onCancel}>Cancel</button>
        {error && <span className="err">{error}</span>}
      </div>
    </form>
  );
}

export function Members({ chapterId, canManage, isAdmin }) {
  const [members, setMembers] = useState(null);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [cpeId, setCpeId] = useState(null);
  const [duesId, setDuesId] = useState(null);
  const [importMsg, setImportMsg] = useState("");
  const fileRef = useRef(null);

  function load() {
    api(`/api/chapters/${chapterId}/members`)
      .then((res) => (res.ok ? res.json() : { members: [] }))
      .then((data) => setMembers(data.members));
  }

  useEffect(load, [chapterId]);

  async function importFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    setImportMsg("Importing…");
    const text = await file.text();
    const res = await api(`/api/chapters/${chapterId}/members/import`, {
      method: "POST",
      body: JSON.stringify({ csv: text }),
    });
    const body = await res.json().catch(() => ({}));
    if (fileRef.current) fileRef.current.value = "";
    if (res.ok) {
      setImportMsg(`Imported: ${body.created} new, ${body.updated} updated, ${body.skipped} skipped.`);
      load();
    } else {
      setImportMsg(body.error || "Import failed.");
    }
  }

  async function reset2fa(member) {
    const msg =
      `Reset two-factor authentication for ${member.name}? Use this only for a lost ` +
      `authenticator — they'll sign in with just their email link until they set it up again.`;
    if (!window.confirm(msg)) return;
    const res = await api(`/api/auth/mfa/admin-reset`, {
      method: "POST",
      body: JSON.stringify({ email: member.email }),
    });
    if (!res.ok) window.alert((await res.json().catch(() => ({}))).error || "Reset failed.");
    load();
  }

  async function remove(member) {
    const msg =
      `Erase ${member.name} and ALL their data (RSVPs, dues, CPE, group ` +
      `memberships, mentoring)? This is a permanent GDPR erasure and cannot be undone.`;
    if (!window.confirm(msg)) return;
    await api(`/api/members/${member.id}`, { method: "DELETE" });
    load();
  }

  if (members === null) {
    return <p className="muted">Loading members…</p>;
  }

  const needle = q.trim().toLowerCase();
  const filtered = members.filter((m) => {
    const matchesQ =
      !needle ||
      m.name.toLowerCase().includes(needle) ||
      (m.email || "").toLowerCase().includes(needle);
    const matchesStage = !stage || m.lifecycle_stage === stage;
    return matchesQ && matchesStage;
  });

  return (
    <>
      <div className="section-head" style={{ gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <input
            name="search"
            placeholder="Search name or email"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ width: 220 }}
          />
          <select name="stage" value={stage} onChange={(e) => setStage(e.target.value)} style={{ width: "auto" }}>
            <option value="">All stages</option>
            {STAGES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <span className="muted" style={{ fontSize: 13 }}>
            {filtered.length} of {members.length}
          </span>
        </div>
        {canManage && (
          <div style={{ display: "flex", gap: 8 }}>
            <a className="btn-ghost" style={{ padding: "10px 14px", borderRadius: 8, textDecoration: "none" }} href={`/api/chapters/${chapterId}/members/export`}>
              Export CSV
            </a>
            <button className="btn-ghost" onClick={() => fileRef.current?.click()} type="button">
              Import CSV
            </button>
            <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={importFile} style={{ display: "none" }} />
            <button className="btn-primary" style={{ width: "auto" }} onClick={() => setAdding(!adding)} type="button">
              {adding ? "Close" : "Add member"}
            </button>
          </div>
        )}
      </div>

      {importMsg && <div className="muted" style={{ fontSize: 13, marginBottom: 10 }}>{importMsg}</div>}
      {adding && <AddForm chapterId={chapterId} onAdded={() => { setAdding(false); load(); }} />}

      <div style={{ marginTop: 14 }}>
        {filtered.length ? (
          <table className="tbl">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Segment</th>
                <th>Stage</th>
                <th>Type</th>
                <th>Source</th>
                {canManage && <th />}
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <Fragment key={m.id}>
                  <tr>
                    <td>
                      <strong>{m.name}</strong>
                      {m.has_2fa && <span title="Two-factor authentication on"> 🔒</span>}
                      {m.dietary_requirements && (
                        <>
                          <br />
                          <span className="muted" style={{ fontSize: 12 }}>🍽 {m.dietary_requirements}</span>
                        </>
                      )}
                    </td>
                    <td className="muted">{m.email || "—"}</td>
                    <td>{m.segment || "—"}</td>
                    <td>{m.lifecycle_stage || "—"}</td>
                    <td>{m.membership_type || "—"}</td>
                    <td><span className="pill">{m.source}</span></td>
                    {canManage && (
                      <td className="row-actions">
                        <button className="btn-ghost" onClick={() => setDuesId(duesId === m.id ? null : m.id)} type="button">
                          {duesId === m.id ? "Close" : "Dues"}
                        </button>
                        <button className="btn-ghost" onClick={() => setCpeId(cpeId === m.id ? null : m.id)} type="button">
                          {cpeId === m.id ? "Close" : "CPE"}
                        </button>
                        <button className="btn-ghost" onClick={() => setEditingId(editingId === m.id ? null : m.id)} type="button">
                          {editingId === m.id ? "Close" : "Edit"}
                        </button>
                        <a className="btn-ghost" href={`/api/members/${m.id}/data-export`} title="Download all data held on this member (GDPR access)">Data</a>
                        {isAdmin && m.has_2fa && (
                          <button className="btn-ghost" onClick={() => reset2fa(m)} type="button" title="Reset this member's 2FA (lost authenticator)">Reset 2FA</button>
                        )}
                        <button className="btn-danger" onClick={() => remove(m)} type="button">Erase</button>
                      </td>
                    )}
                  </tr>
                  {editingId === m.id && (
                    <tr>
                      <td colSpan={canManage ? 7 : 6} style={{ background: "var(--surface-2)" }}>
                        <MemberEditForm member={m} onSaved={() => { setEditingId(null); load(); }} onCancel={() => setEditingId(null)} />
                      </td>
                    </tr>
                  )}
                  {cpeId === m.id && (
                    <tr>
                      <td colSpan={canManage ? 7 : 6} style={{ background: "var(--surface-2)" }}>
                        <MemberCpe member={m} canManage={canManage} />
                      </td>
                    </tr>
                  )}
                  {duesId === m.id && (
                    <tr>
                      <td colSpan={canManage ? 7 : 6} style={{ background: "var(--surface-2)" }}>
                        <MemberDues member={m} canManage={canManage} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="empty">
            {members.length ? "No members match your search." : "No members yet — add one or import a CSV."}
          </p>
        )}
      </div>
    </>
  );
}
